import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, Lock, Crown, Grid3X3, Gift, Copy } from 'lucide-react';
import {
  calculatePythagorasSquare,
  getInterpretation,
  getLineInterpretation,
  calculateLineSum,
  SQUARE_LABELS,
  SQUARE_EXTENDED_LABELS,
  SQUARE_LINES
} from '@/utils/pythagorasSquare';
import VIPModal from '@/components/VIPModal';
import { copyToClipboard } from '@/utils/copyToClipboard';

export default function PythagorasSquare({ day, month, year, isVIP = false, onVIPSuccess, userData }) {
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const [showVIPModal, setShowVIPModal] = useState(false);
  
  const { square, additionalNumbers } = calculatePythagorasSquare(day, month, year);
  
  // Preliudijos užrakintiems langeliams
  const getCellPrelude = (position) => {
    const preludes = {
      2: 'Energija – tai jūsų gyvenimo variklis. Šis langelis atskleidžia, kiek vidinės galios turite veikti, kurti ir siekti tikslų. Ar esate energingas veikėjas, ar mėgstate ramesnį tempą?',
      3: 'Tvarka ir Sistemos – jūsų gebėjimas organizuoti gyvenimą. Šis skaičius parodo, ar jums patinka aiškios struktūros ir rutinos, ar labiau vertinate spontaniškumą ir laisvę.',
      4: 'Sveikata – jūsų fizinio kūno atsparumas ir energija. Šis langelis atskleidžia, kokią natūralią fizinę konstituciją turite ir kaip turėtumėte rūpintis savo kūnu.',
      5: 'Vidinis Kompasas – jūsų intuicija ir logika. Čia slypi atsakymas, kaip jūs priimate sprendimus: ar vadovaujatės vidiniu balsu, ar analize ir faktais.',
      6: 'Meistriškumas – jūsų rankų darbas ir praktiniai įgūdžiai. Šis skaičius parodo, ar turite "auksinių rankų" ir ar galite išmokti bet kokio amato.',
      7: 'Sėkmė – jūsų santykis su likimo dovana. Šis langelis atskleidžia, ar gimėte „su marškinėliais", ar sėkmę turite užsidirbti sunkiu darbu.',
      8: 'Pareiga – jūsų atsakomybės jausmas ir ištikimybė. Šis skaičius parodo, kaip stipriai jaučiatės įsipareigoję šeimai, darbui ir artimiesiems.',
      9: 'Išmintis – jūsų gyvenimo patirtis ir protas. Čia slypi jūsų potencialas tapti mokytoju, patarėju ar dvasiniu vadovu kitiems.'
    };
    return preludes[position] || 'Šis langelis atskleidžia svarbią jūsų asmenybės dalį.';
  };
  
  // Skaičių rodymas (pvz., 2222 jei yra 4 dvejetai, - jei nėra)
  const getDisplayValue = (position, count) => {
    if (count === 0) return '-';
    // Rodyti pozicijos skaičių tiek kartų, kiek jo yra (pvz., 2222 jei 4 dvejetai)
    const digit = position.toString();
    return digit.repeat(Math.min(count, 6));
  };
  
  // Grid pozicijos
  const gridPositions = [
    [1, 4, 7],
    [2, 5, 8],
    [3, 6, 9]
  ];
  
  // Spalvų gradientas pagal skaičių kiekį
  const getCellColor = (count) => {
    if (count === 0) return 'bg-secondary/30';
    if (count === 1) return 'bg-blue-500/20 border-blue-500/30';
    if (count === 2) return 'bg-green-500/20 border-green-500/30';
    if (count === 3) return 'bg-yellow-500/20 border-yellow-500/30';
    if (count >= 4) return 'bg-primary/20 border-primary/30';
    return 'bg-secondary/30';
  };
  
  // Ar galima matyti interpretaciją (1 langelis nemokamas)
  const canViewInterpretation = (position) => {
    return position === 1 || isVIP;
  };

  return (
    <Card className="card-mystical p-6 border-primary/30 overflow-hidden">
      <div className="flex items-center gap-2 mb-4">
        <Grid3X3 className="w-6 h-6 text-primary" />
        <h3 className="font-display text-xl text-primary">Sielos Matrica</h3>
        <span className="text-xs text-muted-foreground">(Psichomatrica)</span>
      </div>
      
      {/* 3x3 Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4 max-w-xs mx-auto">
        {gridPositions.map((row, rowIndex) => (
          row.map((position) => (
            <button
              key={position}
              onClick={() => setSelectedCell(selectedCell === position ? null : position)}
              className={`
                relative aspect-square rounded-lg border-2 transition-all duration-200
                flex flex-col items-center justify-center p-2
                hover:scale-105 cursor-pointer
                ${getCellColor(square[position])}
                ${selectedCell === position ? 'ring-2 ring-primary' : ''}
              `}
            >
              <span className="text-[10px] text-muted-foreground mb-1">
                {SQUARE_LABELS[position]}
              </span>
              <span className="text-lg font-bold text-foreground">
                {getDisplayValue(position, square[position])}
              </span>
              <span className="text-[10px] text-primary font-medium">
                ({square[position]})
              </span>
              
              {/* Lock icon for non-VIP */}
              {!canViewInterpretation(position) && (
                <div className="absolute top-1 right-1">
                  <Lock className="w-3 h-3 text-muted-foreground/50" />
                </div>
              )}
            </button>
          ))
        ))}
      </div>
      
      {/* Legenda */}
      <div className="flex justify-center gap-4 text-xs text-muted-foreground mb-4">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-secondary/30"></div> Nėra
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500/30"></div> 1
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/30"></div> 2
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500/30"></div> 3
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-primary/20 border border-primary/30"></div> 4+
        </span>
      </div>
      
      {/* Selected Cell Interpretation */}
      {selectedCell && (
        <div className="bg-secondary/20 border border-primary/20 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-primary font-semibold">
                {selectedCell}. {SQUARE_LABELS[selectedCell]}
              </span>
              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                {square[selectedCell]} {square[selectedCell] === 1 ? 'skaičius' : 'skaičiai'}
              </span>
            </div>
            {canViewInterpretation(selectedCell) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copyToClipboard(
                  `${selectedCell}. ${SQUARE_LABELS[selectedCell]} (${square[selectedCell]} ${square[selectedCell] === 1 ? 'skaičius' : 'skaičiai'})\n\n✨ ${SQUARE_EXTENDED_LABELS[selectedCell]}\n\n${getInterpretation(selectedCell, square[selectedCell])}`,
                  SQUARE_LABELS[selectedCell]
                )}
                className="text-primary hover:text-primary-glow hover:bg-primary/10"
              >
                <Copy className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Išplėstinis pavadinimas */}
          <p className="text-amber-400/90 text-sm italic mb-3">
            ✨ {SQUARE_EXTENDED_LABELS[selectedCell]}
          </p>
          
          {canViewInterpretation(selectedCell) ? (
            <p className="text-foreground/80 text-sm leading-relaxed">
              {getInterpretation(selectedCell, square[selectedCell])}
            </p>
          ) : (
            <div className="space-y-3">
              {/* Preliudija - trumpas paaiškinimas */}
              <p className="text-foreground/70 text-sm leading-relaxed italic">
                {getCellPrelude(selectedCell)}
              </p>
              <div className="flex items-center gap-2 text-muted-foreground pt-2 border-t border-border/50">
                <Lock className="w-4 h-4" />
                <span className="text-sm">
                  Pilna interpretacija prieinama VIP nariams
                </span>
                <Button
                  size="sm"
                  onClick={() => setShowVIPModal(true)}
                  className="ml-auto bg-primary hover:bg-primary-glow text-xs"
                >
                  <Crown className="w-3 h-3 mr-1" />
                  Atrakinti
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Lines Section */}
      {isVIP && (
        <div className="mt-4">
          <h4 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            Linijų analizė
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(SQUARE_LINES).map(([key, line]) => {
              const sum = calculateLineSum(square, line.positions);
              return (
                <button
                  key={key}
                  onClick={() => setSelectedLine(selectedLine === key ? null : key)}
                  className={`
                    p-2 rounded-lg border text-left transition-all
                    ${selectedLine === key 
                      ? 'bg-primary/20 border-primary' 
                      : 'bg-secondary/10 border-border/50 hover:bg-secondary/20'
                    }
                  `}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-foreground">{line.name}</span>
                    <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                      {sum}
                    </span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{line.positions.join('-')}</span>
                </button>
              );
            })}
          </div>
          
          {/* Selected Line Interpretation */}
          {selectedLine && (
            <div className="bg-secondary/20 border border-primary/20 rounded-lg p-3 mt-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-primary font-semibold text-sm">
                    {SQUARE_LINES[selectedLine].name}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    ({SQUARE_LINES[selectedLine].positions.join(' + ')})
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(
                    `${SQUARE_LINES[selectedLine].name} (${SQUARE_LINES[selectedLine].positions.join(' + ')})\n\n${getLineInterpretation(selectedLine, calculateLineSum(square, SQUARE_LINES[selectedLine].positions))}`,
                    SQUARE_LINES[selectedLine].name
                  )}
                  className="text-primary hover:text-primary-glow hover:bg-primary/10 h-6 w-6 p-0"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-foreground/80 text-xs leading-relaxed">
                {getLineInterpretation(selectedLine, calculateLineSum(square, SQUARE_LINES[selectedLine].positions))}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* VIP Upsell for non-VIP users */}
      {!isVIP && (
        <div className="mt-4 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/30 rounded-lg p-4 text-center overflow-hidden">
          <Crown className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-sm text-foreground mb-1">
            <strong>Pilna Sielos Matricos analizė</strong>
          </p>
          <p className="text-xs text-muted-foreground mb-2">
            8 papildomi langeliai + 8 linijų analizė
          </p>
          <div className="flex items-center justify-center gap-1 mb-3 text-xs text-primary">
            <Gift className="w-4 h-4" />
            <span className="font-semibold">DOVANA: Sielos žemėlapio archyvas</span>
          </div>
          <Button
            onClick={() => setShowVIPModal(true)}
            size="lg"
            className="w-full sm:w-auto bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-500 hover:via-yellow-500 hover:to-amber-600 text-black font-bold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 px-6"
          >
            <Gift className="w-5 h-5 mr-2" />
            Noriu matyti visą paveikslą ✨
          </Button>
        </div>
      )}

      {/* VIP Modal */}
      <VIPModal
        isOpen={showVIPModal}
        onClose={() => setShowVIPModal(false)}
        onSuccess={onVIPSuccess}
        userData={userData}
      />
    </Card>
  );
}
